import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useAppDispatch } from '@verantos-dev/app-state-base';
import {
  selectConceptGroupSelections,
  useCohortDefinitionStore,
} from '@verantos-dev/app-state-cohort-definition';
import {
  addResearchAssistantMessages,
  emitCreateConceptGroupFromSelection,
  useSetResearchAssistantToProcessing,
} from '@verantos-dev/app-state-research-assistant';
import type { SuggestedAction } from '@verantos-dev/shared-components';

export const useConceptGroupSuggestedActions = ({
  onUserPromptChange,
}: {
  onUserPromptChange: (prompt: string) => void;
}): SuggestedAction[] | undefined => {
  const dispatch = useAppDispatch();
  const setResearchAssistantToProcessing = useSetResearchAssistantToProcessing();
  const intl = useIntl();

  const conceptGroupSelections = useCohortDefinitionStore(selectConceptGroupSelections);
  const markConceptGroupSelectionsAsReported = useCohortDefinitionStore(
    (state) => state.markConceptGroupSelectionsAsReported
  );

  const conceptGroupSelectionsWithIssues = useMemo(
    () =>
      conceptGroupSelections?.filter(
        (s) => (s.status === 'warning' || s.status === 'error') && !s.reported
      ),
    [conceptGroupSelections]
  );

  const actions = useMemo(() => {
    if (!conceptGroupSelectionsWithIssues || conceptGroupSelectionsWithIssues.length === 0) {
      return undefined;
    }

    return conceptGroupSelectionsWithIssues.map((selection) => ({
      id: selection.filterKey,
      label: intl.formatMessage(
        {
          id: 'projects.researchAssistant.suggestedActions.createConceptGroup',
        },
        {
          conceptGroupName: selection.span,
        }
      ),
      onClick: () => {
        const prompt = intl.formatMessage(
          {
            id: 'projects.researchAssistant.suggestedActions.createConceptGroupPrompt',
          },
          {
            conceptGroupName: selection.span,
          }
        );

        setResearchAssistantToProcessing();

        dispatch(
          addResearchAssistantMessages([
            {
              message: prompt,
              timestamp: new Date().toLocaleTimeString(),
              currentUser: '',
              isCurrentUser: true,
            },
          ])
        );

        markConceptGroupSelectionsAsReported({ filterKeys: [selection.filterKey] });

        emitCreateConceptGroupFromSelection({
          anchor_id: selection.anchorId,
          filter_key: selection.filterKey,
          span: selection.span,
        });
      },
    }));
  }, [
    conceptGroupSelectionsWithIssues,
    dispatch,
    setResearchAssistantToProcessing,
    intl,
    markConceptGroupSelectionsAsReported,
  ]);

  return actions;
};
